/**
 * Dark Theme Definition
 * Based on audit requirements: Dark (#1f2937, #22c55e)
 */

import type { Theme, ColorScheme } from '@atlas-forms/types-js';

/**
 * Dark color scheme
 */
export const darkColorScheme: ColorScheme = {
  // Primary colors
  primary: '#22c55e',
  primaryLight: '#4a9d57',
  primaryDark: '#2a6c35',

  // Secondary colors
  secondary: '#0891b2',
  secondaryLight: '#1ab2d1',
  secondaryDark: '#067b93',

  // Neutral colors
  background: '#1f2937',
  surface: '#374151',
  surfaceLight: '#4b5563',

  // Text colors
  textPrimary: '#f3f4f6',
  textSecondary: '#d1d5db',
  textMuted: '#9ca3af',

  // Borders and dividers
  border: '#4b5563',
  borderLight: '#6b7280',

  // Status colors
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#0891b2',

  // Interactive states
  hover: '#4b5563',
  active: '#6b7280',
  disabled: '#6b7280',
  disabledText: '#9ca3af',

  // Semantic colors
  danger: '#dc2626',
  caution: '#ea580c',
  positive: '#059669',
};

/**
 * Dark theme definition
 */
export const darkTheme: Theme = {
  name: 'dark',
  colors: darkColorScheme,
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
  },
  radius: {
    none: '0',
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.2)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
  },
  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    dropdown: 1000,
    sticky: 1010,
    fixed: 1020,
    backdrop: 1030,
    offcanvas: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
  transitions: {
    fast: '150ms',
    base: '250ms',
    slow: '350ms',
  },
};
